import React from 'react';

interface HintingViewProps {
  revealedLetters: string;
  hints: any[];
  playerId: string | null;
  isMaster: boolean;
  hintTimeRemaining: number;
  hintTotalTime: number | null;
  hintTimerComplete: boolean;
  secretWord?: string;
  playerNames?: Record<string, { name: string; isOnline: boolean }>;
  onSubmitHint: (hint: string) => void;
  onChooseHint?: (hintId: string) => void;
  onVoteHint?: (hintId: string) => void;
}

// Sub-component: Hint Card
const HintCard: React.FC<{
  hint: any;
  isMaster: boolean;
  isExpanded: boolean;
  playerNames: Record<string, { name: string; isOnline: boolean }>;
  playerId: string | null;
  onToggleExpand: () => void;
  onChooseHint?: (hintId: string) => void;
  onVoteHint?: (hintId: string) => void;
}> = ({ hint, isMaster, isExpanded, playerNames, playerId, onToggleExpand, onChooseHint, onVoteHint }) => {
  const voters = hint.votes || [];
  const hasVoted = voters.includes(playerId);
  const maxLength = 70;
  const isTruncated = hint.text.length > maxLength;
  const displayText = !isExpanded && isTruncated ? hint.text.substring(0, maxLength) + '...' : hint.text;
  
  return (
    <div
      style={{
        background: 'rgba(255, 255, 255, 0.15)',
        borderRadius: '12px'
      }}
    >
      {/* Main hint row */}
      <div
        onClick={onToggleExpand}
        style={{
          padding: '12px 16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '12px',
          cursor: 'pointer'
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <p style={{
            color: 'white',
            fontSize: '15px',
            fontWeight: '600',
            margin: 0,
            wordWrap: 'break-word',
            whiteSpace: isExpanded ? 'normal' : 'nowrap',
            overflow: isExpanded ? 'visible' : 'hidden',
            textOverflow: isExpanded ? 'clip' : 'ellipsis'
          }}>
            {displayText}
          </p>
        </div>
        
        {!isExpanded && (
          <>
            {!isMaster && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (!hasVoted) {
                    onVoteHint?.(hint.id);
                  }
                }}
                disabled={hasVoted}
                style={{
                  background: hasVoted ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '20px',
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  border: 'none',
                  cursor: hasVoted ? 'not-allowed' : 'pointer',
                  whiteSpace: 'nowrap',
                  opacity: hasVoted ? 0.5 : 1
                }}
              >
                <span style={{ fontSize: '14px' }}>✋</span>
                <span style={{
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '600'
                }}>
                  Contact {hint.voteCount || 0}
                </span>
              </button>
            )}
            
            {isMaster && (
              <>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.2)',
                  borderRadius: '20px',
                  padding: '6px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <span style={{ fontSize: '14px' }}>✋</span>
                  <span style={{
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: '600'
                  }}>
                    {hint.voteCount || 0}
                  </span>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onChooseHint?.(hint.id);
                  }}
                  style={{
                    background: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '14px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Choose
                </button>
              </>
            )}
          </>
        )}
      </div>
      
      {/* Expanded hint text and actions */}
      {isExpanded && (
        <div style={{
          padding: '12px 16px',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>

          
          {/* Action buttons and voter info */}
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px'
          }}>
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              flex: 1
            }}>
              {voters.length > 0 && (
                <>
                  <p style={{
                    fontSize: '11px',
                    fontWeight: '700',
                    color: 'rgba(255, 255, 255, 0.6)',
                    letterSpacing: '1px',
                    margin: '0 0 4px 0'
                  }}>
                    VOTERS ({voters.length})
                  </p>
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '4px',
                    maxHeight: '120px',
                    overflowY: voters.length > 3 ? 'scroll' : 'visible',
                    WebkitOverflowScrolling: 'touch',
                    paddingRight: '4px'
                  }}>
                    {voters.map((voterId: string, index: number) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          fontSize: '12px',
                          color: 'rgba(255, 255, 255, 0.8)'
                        }}
                      >
                        <span>👤</span>
                        <span>{playerNames[voterId]?.name || `Player ${voterId.slice(0, 4)}`}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              alignItems: 'flex-end',
              minWidth: 'auto'
            }}>
              {!isMaster && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    if (!hasVoted) {
                      onVoteHint?.(hint.id);
                    }
                  }}
                  disabled={hasVoted}
                  style={{
                    background: hasVoted ? 'rgba(255, 255, 255, 0.1)' : 'rgba(255, 255, 255, 0.2)',
                    borderRadius: '20px',
                    padding: '6px 12px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    border: 'none',
                    cursor: hasVoted ? 'not-allowed' : 'pointer',
                    opacity: hasVoted ? 0.5 : 1
                  }}
                >
                  <span style={{ fontSize: '14px' }}>✋</span>
                  <span style={{
                    color: 'white',
                    fontSize: '13px',
                    fontWeight: '600'
                  }}>
                    Contact {hint.voteCount || 0}
                  </span>
                </button>
              )}
              
              {isMaster && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onChooseHint?.(hint.id);
                  }}
                  style={{
                    background: '#10B981',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer'
                  }}
                >
                  Choose
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Sub-component: Hints List
const HintsList: React.FC<{
  hints: any[];
  isMaster: boolean;
  expandedHintId: string | null;
  playerNames: Record<string, { name: string; isOnline: boolean }>;
  playerId: string | null;
  onToggleHint: (hintId: string) => void;
  onChooseHint?: (hintId: string) => void;
  onVoteHint?: (hintId: string) => void;
}> = ({ hints, isMaster, expandedHintId, playerNames, playerId, onToggleHint, onChooseHint, onVoteHint }) => (
  <div>
    <p style={{
      fontSize: '11px',
      fontWeight: '700',
      color: 'rgba(255, 255, 255, 0.7)',
      letterSpacing: '1.5px',
      textAlign: 'left',
      marginBottom: '12px'
    }}>
      SUBMITTED HINTS
    </p>
    <div style={{
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      padding: '16px',
      minHeight: '80px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      maxHeight: '300px',
      overflowY: 'auto',
      WebkitOverflowScrolling: 'touch'
    }}>
      {hints.length === 0 ? (
        <p style={{
          color: 'rgba(255, 255, 255, 0.5)',
          textAlign: 'center',
          fontSize: '14px',
          margin: 'auto'
        }}>
          No hints yet...
        </p>
      ) : (
        hints.map((hint, index) => (
          <HintCard
            key={index}
            hint={hint}
            isMaster={isMaster}
            isExpanded={expandedHintId === hint.id}
            playerNames={playerNames}
            playerId={playerId}
            onToggleExpand={() => onToggleHint(hint.id)}
            onChooseHint={onChooseHint}
            onVoteHint={onVoteHint}
          />
        ))
      )}
    </div>
  </div>
);

export const HintingView: React.FC<HintingViewProps> = ({
  revealedLetters,
  hints,
  playerId,
  isMaster,
  hintTimeRemaining,
  hintTotalTime,
  hintTimerComplete,
  secretWord = '',
  playerNames = {},
  onSubmitHint,
  onChooseHint,
  onVoteHint
}) => {
  const [hintText, setHintText] = React.useState('');
  const [hasSubmittedHint, setHasSubmittedHint] = React.useState(false);
  const [expandedHintId, setExpandedHintId] = React.useState<string | null>(null);

  const handleToggleHint = (hintId: string) => {
    setExpandedHintId(prev => prev === hintId ? null : hintId);
  };

  const userHint = hints.find(h => h.playerId === playerId);
  const hasSubmitted = hasSubmittedHint || !!userHint;

  const isHintValid = (text: string) => {
    const trimmed = text.trim();
    return trimmed.length >= 2 && trimmed.length <= 100;
  };

  const handleSubmitHint = () => {
    if (isHintValid(hintText)) {
      onSubmitHint(hintText);
      setHasSubmittedHint(true);
      setHintText('');
    }
  };

  return (
    <div style={{
      width: '100%',
      height: '100vh',
      background: 'linear-gradient(180deg, #6366f1 0%, #a855f7 50%, #ec4899 100%)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      padding: '40px 20px',
      overflow: 'auto',
      position: 'relative'
    }}>
      {/* Title */}
      <h1 style={{
        fontSize: '24px',
        fontWeight: '600',
        color: 'rgba(255, 255, 255, 0.8)',
        textAlign: 'center',
        margin: '0 0 24px 0',
        width: '100%'
      }}>
        Guess the word
      </h1>

      {/* Your Word Section */}
      <div style={{
        background: 'rgba(255, 255, 255, 0.15)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        width: '100%',
        maxWidth: '400px'
      }}>
        <div style={{ flex: 1 }}>
          <p style={{
            fontSize: '11px',
            fontWeight: '700',
            color: 'rgba(255, 255, 255, 0.7)',
            letterSpacing: '1.5px',
            margin: '0 0 8px 0',
            alignContent: 'center',
            display: 'flex',
            justifyContent: 'center'
          }}>
            {isMaster ? 'YOUR WORD' : 'REVEALED'}
          </p>
          
          {isMaster && secretWord ? (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '4px',
              flexWrap: 'wrap',
              fontFamily: 'monospace'
            }}>
              {secretWord.split('').map((fullChar, index) => {
                const revealedChar = revealedLetters[index] || ' ';
                const isRevealed = revealedChar !== ' ' && revealedChar !== '_';
                
                return (
                  <div
                    key={index}
                    style={{
                      position: 'relative',
                      width: '40px',
                      height: '50px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {/* Background: unrevealed letter at low opacity */}
                    <span style={{
                      position: 'absolute',
                      fontSize: '40px',
                      fontWeight: 'bold',
                      color: 'rgba(255, 255, 255, 0.2)',
                      margin: 0
                    }}>
                      {fullChar}
                    </span>
                    
                    {/* Foreground: revealed letter */}
                    {isRevealed && (
                      <span style={{
                        position: 'absolute',
                        fontSize: '40px',
                        fontWeight: 'bold',
                        color: 'white',
                        margin: 0
                      }}>
                        {revealedChar}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <p style={{
              fontSize: '40px',
              fontWeight: 'bold',
              color: 'white',
              margin: 0,
              letterSpacing: '4px',
              wordBreak: 'break-all',
              alignContent: 'center',
              display: 'flex',
              justifyContent: 'center'
            }}>
              {revealedLetters}
            </p>
          )}
        </div>
        
      </div>

      {/* Timer Section */}
      <div style={{
        marginBottom: '24px',
        width: '100%',
        maxWidth: '400px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <span style={{
            fontSize: '16px',
            color: 'rgba(255, 255, 255, 0.8)'
          }}>
            Time remaining:
          </span>
          <span style={{
            fontSize: '18px',
            fontWeight: 'bold',
            color: 'white'
          }}>
            {hintTimeRemaining}s
          </span>
        </div>
        <div style={{
          width: '100%',
          height: '6px',
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '3px',
          overflow: 'hidden'
        }}>
          <div style={{
            width: `${hintTotalTime ? (hintTimeRemaining / hintTotalTime) * 100 : 0}%`,
            height: '100%',
            background: 'white',
            transition: 'width 1s linear'
          }} />
        </div>
      </div>

      {/* Submitted Hints Section - Shared Component */}
      <div style={{
        marginBottom: isMaster ? '0' : '20px',
        width: '100%',
        maxWidth: '400px'
      }}>
        <HintsList
          hints={hints}
          isMaster={isMaster}
          expandedHintId={expandedHintId}
          playerNames={playerNames}
          playerId={playerId}
          onToggleHint={handleToggleHint}
          onChooseHint={onChooseHint}
          onVoteHint={onVoteHint}
        />
      </div>

      {/* Non-Master: Input and Status */}
      {!isMaster && (
        <div style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'flex-end',
          width: '100%',
          maxWidth: '400px'
        }}>
          {hasSubmitted ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              padding: '16px',
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '12px'
            }}>
              <span style={{ fontSize: '20px' }}>✨</span>
              <span style={{
                fontSize: '16px',
                fontWeight: '600',
                color: 'white'
              }}>
                Hint submitted! Waiting for others...
              </span>
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '12px',
              width: '100%'
            }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '4px'
              }}>
                <label style={{
                  fontSize: '16px',
                  fontWeight: '600',
                  color: 'white'
                }}>
                  Your Hint
                </label>
                <span style={{
                  fontSize: '14px',
                  color: 'rgba(255, 255, 255, 0.7)'
                }}>
                  {hintText.length}/100
                </span>
              </div>
              <input
                type="text"
                placeholder="Enter your hint"
                value={hintText}
                onChange={(e) => {
                  if (e.target.value.length <= 100) {
                    setHintText(e.target.value);
                  }
                }}
                maxLength={100}
                disabled={hintTimerComplete}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'white',
                  color: '#333',
                  fontFamily: 'inherit',
                  boxSizing: 'border-box'
                }}
              />
              <button
                onClick={handleSubmitHint}
                disabled={!isHintValid(hintText) || hintTimerComplete}
                style={{
                  width: '100%',
                  padding: '16px',
                  fontSize: '18px',
                  fontWeight: '600',
                  borderRadius: '12px',
                  border: 'none',
                  background: isHintValid(hintText) && !hintTimerComplete 
                    ? '#10B981' 
                    : 'rgba(255, 255, 255, 0.3)',
                  color: 'white',
                  cursor: isHintValid(hintText) && !hintTimerComplete 
                    ? 'pointer' 
                    : 'not-allowed',
                  transition: 'all 0.2s'
                }}
              >
                {hintTimerComplete ? "Time's Up" : 'Submit Hint'}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Master: Status */}
      {isMaster && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '16px',
          background: 'rgba(255, 255, 255, 0.2)',
          borderRadius: '12px',
          marginTop: '20px',
          width: '100%',
          maxWidth: '400px'
        }}>
          <span style={{ fontSize: '20px' }}>✨</span>
          <span style={{
            fontSize: '16px',
            fontWeight: '600',
            color: 'white'
          }}>
            Waiting for hints...
          </span>
        </div>
      )}
    </div>
  );
};
